import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE, CHART_TYPE } from '../renderTypes'
import { getTableViewsFilters } from '~/services/explore'
import { OrderValue } from '~/types/cube'
import { SourceType } from '~/types/sources'
import { DashboardModuleType, type DashboardConfig } from '~/types/dashboards'

export const createJobsDashboardConfig = (): DashboardConfig => {
  return [
    {
      titleKey: 'dashboard.jobs.inPeriodMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.jobs.countHire.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_COUNT_HIRE,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_COUNT_HIRE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_COUNT_HIRE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.LARGE_CHART,
          options: {
            config: {
              titleKey: 'dashboard.jobs.countPerStageFunnel.title',
              infoContent: {
                definition:
                  'dashboard.jobs.countPerStageFunnel.info.definition',
                source: SourceType.ATS,
                insight: 'dashboard.jobs.countPerStageFunnel.info.insight'
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.RECRUITMENT_COUNT_APPLY,
                MEASURE_TITLES.RECRUITMENT_COUNT_SCREENING,
                MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEW_1,
                MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEW_2,
                MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEW_3,
                MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEW_4,
                MEASURE_TITLES.RECRUITMENT_COUNT_OFFER,
                MEASURE_TITLES.RECRUITMENT_COUNT_HIRE
              ]
            },
            chartType: CHART_TYPE.PIPE
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.jobs.lastHiredEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.JOB_APPLICATION_CANDIDATE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.DATE_DAY,
                DIMENSION_TITLES.JOB_TITLE
              ],
              columnsTranslationPrefix:
                'dashboard.jobs.lastHiredEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_COUNT_HIRE,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_COUNT_HIRE],
              dimensions: [
                DIMENSION_TITLES.JOB_APPLICATION_CANDIDATE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.DATE_DAY,
                DIMENSION_TITLES.JOB_TITLE
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.RECRUITMENT_COUNT_HIRE
              ),
              order: [[DIMENSION_TITLES.DATE_DAY, OrderValue.DESC]],
              limit: 5
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.jobs.endOfPeriodMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.jobs.openJobs.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_COUNT_END_JOB_OPEN,
                type: EXPLORER_TYPE.LIST
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_COUNT_END_JOB_OPEN
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_COUNT_END_JOB_OPEN]
            }
          }
        },
        {
          moduleType: DashboardModuleType.LARGE_CHART,
          options: {
            config: {
              titleKey: 'dashboard.jobs.countEndPerStageFunnel.title',
              infoContent: {
                definition:
                  'dashboard.jobs.countEndPerStageFunnel.info.definition',
                source: SourceType.ATS,
                insight: 'dashboard.jobs.countEndPerStageFunnel.info.insight'
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.RECRUITMENT_COUNT_END_UNPROCESSED_APPLY,
                MEASURE_TITLES.RECRUITMENT_COUNT_END_SCREENING_IN_PROGRESS,
                MEASURE_TITLES.RECRUITMENT_COUNT_END_INTERVIEW_1_IN_PROGRESS,
                MEASURE_TITLES.RECRUITMENT_COUNT_END_INTERVIEW_2_IN_PROGRESS,
                MEASURE_TITLES.RECRUITMENT_COUNT_END_INTERVIEW_3_IN_PROGRESS,
                MEASURE_TITLES.RECRUITMENT_COUNT_END_INTERVIEW_4_IN_PROGRESS,
                MEASURE_TITLES.RECRUITMENT_COUNT_END_OFFER_IN_PROGRESS
              ]
            },
            chartType: CHART_TYPE.PIPE
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.jobs.oldestPendingOffers.title',
              columnOrder: [
                DIMENSION_TITLES.JOB_APPLICATION_CANDIDATE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.JOB_APPLICATION_OFFER_DATE,
                DIMENSION_TITLES.JOB_TITLE
              ],
              columnsTranslationPrefix:
                'dashboard.jobs.oldestPendingOffers.columns',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_COUNT_END_OFFER_IN_PROGRESS,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.RECRUITMENT_COUNT_END_OFFER_IN_PROGRESS
              ],
              dimensions: [
                DIMENSION_TITLES.JOB_APPLICATION_CANDIDATE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.JOB_APPLICATION_OFFER_DATE,
                DIMENSION_TITLES.JOB_TITLE
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.RECRUITMENT_COUNT_END_OFFER_IN_PROGRESS
              ),
              order: [
                [DIMENSION_TITLES.JOB_APPLICATION_OFFER_DATE, OrderValue.ASC]
              ],
              limit: 5
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.jobs.additionalMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.jobs.countInterviewedCandidates.title',
              exploreLink: {
                measure:
                  MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEWED_CANDIDATES,
                dimension: DIMENSION_TITLES.JOB_APPLICATION_CURRENT_STAGE_NAME,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEWED_CANDIDATES
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.RECRUITMENT_COUNT_INTERVIEWED_CANDIDATES
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.jobs.jobOffers.title',
              exploreLink: {
                measure: MEASURE_TITLES.RECRUITMENT_COUNT_OFFER,
                type: EXPLORER_TYPE.LIST
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.RECRUITMENT_COUNT_OFFER
                ]
            },
            query: {
              measures: [MEASURE_TITLES.RECRUITMENT_COUNT_OFFER]
            }
          }
        }
      ]
    }
  ]
}
